import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { LoadingButton } from "@material-ui/lab";
import {
  CheckCircleOutline,
  MailOutline,
  LockOpen,
  Block,
  VpnKey,
} from "@material-ui/icons";
import { useSelector } from "react-redux";
import { clientesApi } from "src/services/clientesApi";
import { useSnackbar } from "src/components/snackbar";
import { formatearFechaDisplay } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    minWidth: 180,
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  actionsTitle: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chipActivo: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    fontWeight: 600,
  },
  chipInactivo: {
    backgroundColor: "#F5F5F5",
    color: "#757575",
    fontWeight: 600,
  },
}));

const ROLES_ADMIN = ["ADMIN", "SUPER_ADMIN"];

const PortalClienteAcceso = ({ value, cliente }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    rol: rolObtenido,
    sucursal: { refSucursal },
  } = useSelector((state) => state.auth.user);
  const rolUsuario = rolObtenido || "EMPLEADO";
  const esAdmin = ROLES_ADMIN.includes(rolUsuario);

  const { data: portalStatus, isLoading: cargandoStatus } =
    clientesApi.useObtenerPortalStatusQuery(
      { rut: cliente.rut },
      { skip: !cliente.rut || value !== 3 },
    );

  const [invitarPortal, { isLoading: enviandoInvitacion }] =
    clientesApi.useInvitarPortalClienteMutation();

  const [toggleAcceso, { isLoading: togglando }] =
    clientesApi.useToggleAccesoPortalMutation();

  const [resetPassword, { isLoading: enviandoReset }] =
    clientesApi.useResetPasswordPortalDesdeEmpleadoMutation();

  if (value !== 3) return null;

  const portalActivo = portalStatus?.portalActivo ?? false;
  const usuarioSitax = portalStatus?.usuarioSitax ?? null;
  const ultimaActualizacion = portalStatus?.ultimaActualizacion ?? null;

  const handleInvitar = async () => {
    try {
      await invitarPortal({ rut: cliente.rut, refSucursal }).unwrap();
      enqueueSnackbar(
        portalActivo
          ? "Invitación reenviada correctamente"
          : "Invitación enviada y acceso activado",
        {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al enviar la invitación", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  const handleToggleAcceso = async () => {
    try {
      await toggleAcceso({ rut: cliente.rut, refSucursal }).unwrap();
      enqueueSnackbar(
        portalActivo
          ? "Acceso al portal desactivado"
          : "Acceso al portal activado",
        {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al cambiar el estado de acceso",
        {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword({ rut: cliente.rut, refSucursal }).unwrap();
      enqueueSnackbar(
        "Se envió un email al cliente con el link para restablecer su contraseña",
        {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al enviar el email de restablecimiento",
        {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    }
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <div className={classes.headerContainer}>
          <Typography variant="h6">Acceso al Portal</Typography>
          {cargandoStatus ? (
            <Chip
              label="Cargando..."
              size="small"
              className={classes.chipInactivo}
            />
          ) : portalActivo ? (
            <Chip
              icon={<CheckCircleOutline style={{ fontSize: 16 }} />}
              label="Portal Activo"
              size="small"
              className={classes.chipActivo}
            />
          ) : (
            <Chip
              label="Sin acceso"
              size="small"
              className={classes.chipInactivo}
            />
          )}
        </div>

        {/* Info SITAX */}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box className={classes.infoRow}>
              <Typography variant="body2" className={classes.infoLabel}>
                Usuario SITAX:
              </Typography>
              <Typography variant="body2">
                {cargandoStatus ? "—" : usuarioSitax || "No configurado"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.infoRow}>
              <Typography variant="body2" className={classes.infoLabel}>
                Última actualización:
              </Typography>
              <Typography variant="body2">
                {cargandoStatus
                  ? "—"
                  : ultimaActualizacion
                    ? formatearFechaDisplay(ultimaActualizacion, true)
                    : "—"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Acciones */}
        <Typography variant="subtitle2" className={classes.actionsTitle}>
          Acciones
        </Typography>

        <Box>
          {/* Enviar / Reenviar invitación — todos los roles */}
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<MailOutline />}
            loading={enviandoInvitacion}
            disabled={enviandoInvitacion || cargandoStatus}
            onClick={handleInvitar}
            className={classes.actionButton}
          >
            {portalActivo ? "Reenviar invitación" : "Enviar invitación"}
          </LoadingButton>

          {/* Activar / Desactivar — solo ADMIN / SUPER_ADMIN */}
          {esAdmin && (
            <LoadingButton
              variant="outlined"
              color={portalActivo ? "secondary" : "primary"}
              startIcon={portalActivo ? <Block /> : <LockOpen />}
              loading={togglando}
              disabled={togglando || cargandoStatus || !usuarioSitax}
              onClick={handleToggleAcceso}
              className={classes.actionButton}
            >
              {portalActivo ? "Desactivar acceso" : "Activar acceso"}
            </LoadingButton>
          )}

          {/* Restablecer contraseña — solo ADMIN / SUPER_ADMIN */}
          {esAdmin && (
            <LoadingButton
              variant="outlined"
              startIcon={<VpnKey />}
              loading={enviandoReset}
              disabled={enviandoReset || cargandoStatus || !usuarioSitax}
              onClick={handleResetPassword}
              className={classes.actionButton}
            >
              Restablecer contraseña
            </LoadingButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PortalClienteAcceso;
